/**
 * Natural-language date/time parsing for the Telegram bot.
 *
 * Handles both languages, the three calendars, and Persian digit input. The
 * goal is forgiving parsing, not a full NLP pipeline: pull every date token and
 * time token it recognises, ignore the rest, and report what it did not
 * understand so the bot can ask.
 */

import {
  addDays,
  civilToInstant,
  isValidDate,
  today as todayIn,
  toLatinDigits,
  weekdayIndex,
  type CalendarSystem,
  type CivilDate,
} from "../date/convert.js";

export interface ParsedEvent {
  title: string;
  startUtc: string;
  endUtc: string;
  allDay: boolean;
  /** Which calendar the user expressed the date in, if any. */
  calendar: CalendarSystem | null;
  /** Tokens we recognised and removed from the title. */
  consumed: string[];
  warnings: string[];
}

const FA_WEEKDAYS: Record<string, number> = {
  "یکشنبه": 0, "دوشنبه": 1, "سه‌شنبه": 2, "سه شنبه": 2, "چهارشنبه": 3,
  "پنجشنبه": 4, "پنج‌شنبه": 4, "جمعه": 5, "شنبه": 6,
};
const EN_WEEKDAYS: Record<string, number> = {
  sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3, thursday: 4, thu: 4, thur: 4, thurs: 4, friday: 5,
  fri: 5, saturday: 6, sat: 6,
};

const FA_MONTHS: Record<string, number> = {
  "فروردین": 1, "اردیبهشت": 2, "خرداد": 3, "تیر": 4, "مرداد": 5,
  "شهریور": 6, "مهر": 7, "آبان": 8, "آذر": 9, "دی": 10, "بهمن": 11, "اسفند": 12,
};
const EN_MONTHS: Record<string, number> = {
  january: 1, jan: 1, february: 2, feb: 2, march: 3, mar: 3, april: 4, apr: 4,
  may: 5, june: 6, jun: 6, july: 7, jul: 7, august: 8, aug: 8,
  september: 9, sep: 9, sept: 9, october: 10, oct: 10, november: 11, nov: 11,
  december: 12, dec: 12,
};
/** Hijri month names, for `18 Ramadan` style input. */
const HIJRI_MONTHS: Record<string, number> = {
  "محرم": 1, "صفر": 2, "ربیع الاول": 3, "ربیع‌الاول": 3, "ربیع الثاني": 4,
  "ربیع‌الثانی": 4, "جمادي الاول": 5, "جمادی‌الاول": 5, "جمادي الثاني": 6,
  "جمادی‌الثانی": 6, "رجب": 7, "شعبان": 8, "رمضان": 9, "شوال": 10,
  "ذي القعده": 11, "ذی‌القعده": 11, "ذي الحجه": 12, "ذی‌الحجه": 12,
  muharram: 1, safar: 2, "rabi al-awwal": 3, "rabi al-thani": 4,
  "jumada al-ula": 5, "jumada al-akhirah": 6, rajab: 7, shaban: 8,
  ramadan: 9, shawwal: 10, "dhu al-qidah": 11, "dhu al-hijjah": 12,
};

interface TimeSpec {
  hour: number;
  minute: number;
  /** Ends of the 12-hour clock that this token might mean. */
  meridiem: "am" | "pm" | null;
  text: string;
}

const RELATIVE_DAYS: { words: string[]; days: number }[] = [
  { words: ["پس فردا", "فردای بعد"], days: 2 },
  { words: ["فردا", "tomorrow"], days: 1 },
  { words: ["امروز", "today"], days: 0 },
  { words: ["دیروز", "yesterday"], days: -1 },
  { words: ["پریروز"], days: -2 },
];

const HORIZON_DAYS: Record<string, number> = {
  "امروز": 1, "today": 1, "فردا": 1, "tomorrow": 1,
  "هفته": 7, "week": 7, "ماه": 30, "month": 30, "تقویم": 30, "calendar": 30,
};

export interface ParseContext {
  timeZone: string;
  calendar: CalendarSystem;
  now?: Date;
}

/**
 * Word-boundary lookarounds that also work for non-Latin scripts.
 *
 * JavaScript's `\b` is defined in terms of `\w` ([A-Za-z0-9_]), so it never
 * fires next to a Persian or Arabic letter. Matching "فردا" with `\b...\b`
 * silently fails, which is why every pattern below is built from these.
 */
const W_PREV = "(?<![\\p{L}\\p{N}])";
const W_NEXT = "(?![\\p{L}\\p{N}])";

/** Wrap alternatives in language-agnostic word boundaries. */
function word(alternatives: string[]): string {
  return `${W_PREV}(?:${alternatives.join("|")})${W_NEXT}`;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Build a whole regex for a set of words, escaping each one. */
function wordPattern(words: string[]): RegExp {
  return new RegExp(word(words.map(escapeRe)), "gu");
}

export function parseEventText(input: string, ctx: ParseContext): ParsedEvent {
  const warnings: string[] = [];
  const consumed: string[] = [];
  // Normalise to Latin digits and lower case up front, so every pattern below
  // can use plain [0-9] ranges. Persian digits all share one Unicode block, but
  // mixing them inside a single character class is an easy way to get the range
  // order wrong. Titles come out with Latin digits, which is what we store.
  let rest = " " + toLatinDigits(input.trim()).toLowerCase() + " ";
  const now = ctx.now ?? new Date();

  const take = (pattern: RegExp): RegExpExecArray | null => {
    const m = pattern.exec(rest);
    if (!m) return null;
    consumed.push(m[0].trim());
    rest = rest.replace(pattern, " ");
    return m;
  };

  // ---- explicit date ---------------------------------------------------
  let date: CivilDate | null = null;
  let dateCalendar: CalendarSystem | null = null;

  // ISO: 2026-09-29
  const iso = take(/\b(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})\b/);
  if (iso) {
    const g: CivilDate = {
      year: +iso[1]!,
      month: +iso[2]!,
      day: +iso[3]!,
    };
    if (isValidDate(g, "gregorian")) {
      date = g;
      dateCalendar = "gregorian";
    } else warnings.push("iso");
  }

  // Slash form 1405/07/07 - Jalali if the year looks plausible for it.
  if (!date) {
    const slash = take(/\b(\d{3,4})\/(\d{1,2})\/(\d{1,2})\b/);
    if (slash) {
      const y = +slash[1]!;
      const m = +slash[2]!;
      const d = +slash[3]!;
      const asJalali: CivilDate = { year: y, month: m, day: d };
      if (y >= 1178 && y <= 1633 && isValidDate(asJalali, "jalali")) {
        date = asJalali;
        dateCalendar = "jalali";
      } else {
        const g: CivilDate = { year: y, month: m, day: d };
        if (isValidDate(g, "gregorian")) {
          date = g;
          dateCalendar = "gregorian";
        } else warnings.push("slash");
      }
    }
  }

  // Named month + day: "Sep 29", "29 مهر", "18 Ramadan", "Sep 29 2026"
  if (!date) {
    const named = findNamedMonth(rest);
    if (named) {
      const candidate = buildNamedDate(
        named.month, named.day, named.year, named.calendar, ctx.timeZone, now,
      );
      if (candidate) {
        date = candidate.date;
        dateCalendar = candidate.calendar;
        take(new RegExp(escapeRe(named.raw), "g"));
      } else warnings.push("named");
    }
  }

  // Persian relative day: "فردا", "امروز", "دیروز"
  if (!date) {
    for (const { words, days } of RELATIVE_DAYS) {
      const pattern = wordPattern(words);
      if (pattern.test(rest)) {
        const anchor = todayIn("jalali", ctx.timeZone, now);
        date = addDays(anchor, days, "jalali");
        dateCalendar = "jalali";
        take(pattern);
        break;
      }
    }
  }

  // Weekday: "شنبه" / "Saturday" - next occurrence.
  if (!date) {
    const wd = findWeekday(rest);
    if (wd !== null) {
      const anchor = todayIn("jalali", ctx.timeZone, now);
      const current = weekdayIndex(anchor, "jalali");
      // 0 = Sunday, so the target may already have passed this week.
      const delta = (wd.index - current + 7) % 7 || 7;
      date = addDays(anchor, delta, "jalali");
      dateCalendar = "jalali";
      take(wordPattern([wd.raw]));
    }
  }

  // "in 3 days"
  if (!date) {
    const inDays = take(
      new RegExp(word(["در", "بعد از", "in", "after"]) + "\\s+(\\d{1,3})\\s*" + word(["روز", "days?", "d"]), "u"),
    );
    if (inDays) {
      const anchor = todayIn("jalali", ctx.timeZone, now);
      date = addDays(anchor, +inDays[1]!, "jalali");
      dateCalendar = "jalali";
    }
  }

  if (!date) {
    return {
      title: input.trim(),
      startUtc: "",
      endUtc: "",
      allDay: false,
      calendar: null,
      consumed,
      warnings: ["no_date"],
    };
  }

  // ---- time ------------------------------------------------------------
  // A range such as "۱۰ تا ۱۲" has to be read before findTime: the bare-hour
  // rule would otherwise swallow the first number and leave "تا ۱۲" orphaned.
  // We lift the whole range out of the text and keep its start as the time.
  const rangeRe = new RegExp(
    `(\\d{1,2})(?::(\\d{2}))?\\s*${word(["تا", "to", "-", "–", "—"])}\\s*(\\d{1,2})(?::(\\d{2}))?`,
    "u",
  );
  let rangeEndMin: number | null = null;
  let rangeStart: TimeSpec | null = null;
  const rangeMatch = rangeRe.exec(rest);
  if (rangeMatch) {
    const startMin = +rangeMatch[1]! * 60 + (rangeMatch[2] ? +rangeMatch[2]! : 0);
    const endMin = +rangeMatch[3]! * 60 + (rangeMatch[4] ? +rangeMatch[4]! : 0);
    if (endMin > startMin) {
      rangeEndMin = endMin;
      rangeStart = {
        hour: Math.floor(startMin / 60) % 24,
        minute: startMin % 60,
        meridiem: null,
        text: rangeMatch[0],
      };
      take(rangeRe);
    }
  }

  const found = findTime(rest);
  if (found) take(found.pattern);
  // A range start is already lifted out of the text, so it needs no take().
  const time: TimeSpec | null = found ?? rangeStart;

  const isNoonKeyword = wordPattern(["ظهر", "noon", "midday"]).test(rest);
  if (isNoonKeyword) take(wordPattern(["ظهر", "noon", "midday"]));

  let hour = time?.hour ?? (isNoonKeyword ? 12 : 9);
  let minute = time?.minute ?? 0;
  const allDay = !time && !isNoonKeyword;

  if (time?.meridiem === "pm" && hour < 12) hour += 12;
  if (time?.meridiem === "am" && hour === 12) hour = 0;
  if (hour > 23 || minute > 59) {
    warnings.push("bad_time");
    hour = 9;
    minute = 0;
  }

  // ---- end time --------------------------------------------------------
  // An all-day event is anchored at local midnight, not at the 09:00 default.
  const startUtc = civilToInstant(
    date,
    allDay ? 0 : hour,
    allDay ? 0 : minute,
    ctx.timeZone,
    dateCalendar ?? "jalali",
  );
  const duration = rangeEndMin !== null
    ? rangeEndMin - (hour * 60 + minute)
    : findDuration(rest) ?? 60;
  const endUtc = allDay
    ? civilToInstant(
        addDays(date, 1, dateCalendar!), 0, 0, ctx.timeZone, dateCalendar!,
      )
    : new Date(new Date(startUtc).getTime() + Math.max(1, duration) * 60000).toISOString();

  const title = cleanTitle(rest);
  if (title.length < 3) warnings.push("title_too_short");

  return {
    title,
    startUtc,
    endUtc,
    allDay,
    calendar: dateCalendar,
    consumed,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Date-only parsing, used by the /convert command
// ---------------------------------------------------------------------------

export type ConvertSource = CalendarSystem | "unknown";

export function parseDateOnly(
  input: string,
  opts: { timeZone?: string; now?: Date } = {},
): {
  date: CivilDate | null;
  source: ConvertSource;
} {
  const text = toLatinDigits(input).toLowerCase().trim();
  const tz = opts.timeZone ?? "UTC";
  const at = opts.now ?? new Date();

  // ISO / slash, Jalali or Gregorian.
  const numeric = /(\d{3,4})[-/.](\d{1,2})[-/.](\d{1,2})/.exec(text);
  if (numeric) {
    const y = +numeric[1]!;
    const m = +numeric[2]!;
    const d = +numeric[3]!;
    const asJalali: CivilDate = { year: y, month: m, day: d };
    if (y >= 1178 && y <= 1633 && isValidDate(asJalali, "jalali")) {
      return { date: asJalali, source: "jalali" };
    }
    const asGregorian: CivilDate = { year: y, month: m, day: d };
    if (isValidDate(asGregorian, "gregorian")) {
      return { date: asGregorian, source: "gregorian" };
    }
    return { date: null, source: "unknown" };
  }

  const named = findNamedMonth(text);
  if (named) {
    const built = buildNamedDate(
      named.month, named.day, named.year, named.calendar, tz, at,
    );
    if (built) return { date: built.date, source: built.calendar };
  }

  return { date: null, source: "unknown" };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface NamedMonth {
  month: number;
  day: number | null;
  year: number | null;
  calendar: CalendarSystem;
  raw: string;
}

function findNamedMonth(text: string): NamedMonth | null {
  const latin = toLatinDigits(text).toLowerCase();

  for (const [name, month] of Object.entries(EN_MONTHS)) {
    const idx = latin.indexOf(name);
    if (idx < 0) continue;
    const before = latin.slice(Math.max(0, idx - 12), idx);
    const after = latin.slice(idx + name.length, idx + name.length + 12);

    const dayAfter = /^\s*(\d{1,2})/.exec(after);
    const dayBefore = /(\d{1,2})\s*$/.exec(before);
    const yearMatch = /(\d{4})/.exec(after) ?? /(\d{4})/.exec(before);

    const day = dayAfter ? +dayAfter[1]! : dayBefore ? +dayBefore[1]! : null;
    return {
      month,
      day,
      year: yearMatch ? +yearMatch[1]! : null,
      calendar: "gregorian",
      raw: sliceRaw(text, name, day, yearMatch?.[1]),
    };
  }

  for (const [name, month] of Object.entries(FA_MONTHS)) {
    const idx = text.indexOf(name);
    if (idx < 0) continue;
    const after = text.slice(idx + name.length, idx + name.length + 12);
    const before = text.slice(Math.max(0, idx - 10), idx);
    const dayAfter = /^\s*(\d{1,2})/.exec(toLatinDigits(after));
    const dayBefore = /(\d{1,2})\s*$/.exec(toLatinDigits(before));
    const yearMatch = /(\d{4})/.exec(after) ?? /(\d{4})/.exec(before);
    const day = dayAfter ? +dayAfter[1]! : dayBefore ? +dayBefore[1]! : null;
    return {
      month,
      day,
      year: yearMatch ? +yearMatch[1]! : null,
      calendar: "jalali",
      raw: sliceRaw(text, name, day, yearMatch?.[1]),
    };
  }

  for (const [name, month] of Object.entries(HIJRI_MONTHS)) {
    const idx = text.indexOf(name);
    if (idx >= 0) {
      const after = toLatinDigits(text.slice(idx + name.length, idx + name.length + 12));
      const day = /^\s*(\d{1,2})/.exec(after);
      return { month, day: day ? +day[1]! : null, year: null, calendar: "hijri", raw: name };
    }
  }

  return null;
}

function sliceRaw(
  text: string,
  name: string,
  day: number | null,
  year: string | undefined,
): string {
  const idx = text.indexOf(name);
  if (idx < 0) return name;
  let start = idx;
  let end = idx + name.length;
  if (day !== null) {
    const around = toLatinDigits(text.slice(Math.max(0, idx - 6), end + 6));
    const m = new RegExp(`\\d{1,2}\\s*${escapeRe(name)}|${escapeRe(name)}\\s*\\d{1,2}`).exec(around);
    if (m && m.index !== undefined) {
      start = Math.max(0, idx - 6) + m.index;
      end = start + m[0].length;
    }
  }
  if (year) {
    const yIdx = text.slice(Math.max(0, start - 6), end + 6).indexOf(year);
    if (yIdx >= 0) {
      const abs = Math.max(0, start - 6) + yIdx;
      start = Math.min(start, abs);
      end = Math.max(end, abs + year.length);
    }
  }
  return text.slice(start, end).trim();
}

/**
 * Resolve a month name + day into a full date.
 *
 * `preferred` comes from the month name itself: Persian month names imply the
 * Solar Hijri calendar, English ones the Gregorian, and the Hijri set its own.
 * Without it we would happily read "۱۵ مهر" as 15 July and silently pick the
 * wrong year.
 */
function buildNamedDate(
  month: number,
  day: number | null,
  year: number | null,
  preferred: CalendarSystem | null,
  tz: string,
  at: Date,
): { date: CivilDate; calendar: CalendarSystem } | null {
  if (day === null) return null;

  if (year !== null) {
    const order = orderCalendars(preferred);
    for (const cal of order) {
      const candidate: CivilDate = { year, month, day };
      if (isValidDate(candidate, cal)) return { date: candidate, calendar: cal };
    }
    return null;
  }

  // No year: use the current year in the implied calendar, rolling forward to
  // next year if this year's copy has already passed.
  const order = orderCalendars(preferred);
  for (const cal of order) {
    const baseYear = todayIn(cal, tz, at).year;
    for (const y of [baseYear, baseYear + 1]) {
      const candidate: CivilDate = { year: y, month, day };
      if (!isValidDate(candidate, cal)) continue;
      if (y === baseYear) {
        const now = todayIn(cal, tz, at);
        const passed =
          candidate.month < now.month ||
          (candidate.month === now.month && candidate.day < now.day);
        if (passed) continue;
      }
      return { date: candidate, calendar: cal };
    }
  }
  return null;
}

function orderCalendars(preferred: CalendarSystem | null): CalendarSystem[] {
  const all: CalendarSystem[] = ["jalali", "gregorian", "hijri"];
  if (!preferred) return all;
  return [preferred, ...all.filter((c) => c !== preferred)];
}

function findWeekday(text: string): { raw: string; index: number } | null {
  for (const [name, index] of Object.entries(FA_WEEKDAYS)) {
    if (wordPattern([name]).test(text)) return { raw: name, index };
  }
  for (const [name, index] of Object.entries(EN_WEEKDAYS)) {
    if (wordPattern([name]).test(text)) return { raw: name, index };
  }
  return null;
}

function findTime(text: string): (TimeSpec & { pattern: RegExp }) | null {
  // `text` has already been normalised to Latin digits / lower case.
  // 14:30, 14.30, 2:30 pm
  const colonRe = new RegExp(
    word(["\\d{1,2}[:.]\\d{2}", "\\d{1,2}[:.]\\d{2}\\s*(?:am|pm|ق\\.?\\s*ظ|ب\\.?\\s*ظ)?"]),
    "u",
  );
  const colon = colonRe.exec(text);
  if (colon) {
    const parts = /(\d{1,2})[:.](\d{2})\s*(am|pm|ق\.?\s*ظ|ب\.?\s*ظ)?/.exec(colon[0]);
    if (parts) {
      return {
        hour: +parts[1]!,
        minute: +parts[2]!,
        meridiem: parseMeridiem(parts[3]),
        text: colon[0],
        pattern: colonRe,
      };
    }
  }

  // Persian "ساعت ۸ ب.ظ" / English "at 8pm" / "@10"
  const atRe = new RegExp(
    `${word(["ساعت", "at", "@"])}\\s*(\\d{1,2})\\s*(am|pm|ق\\.?\\s*ظ|ب\\.?\\s*ظ)?(?!\\d)`,
    "u",
  );
  const at = atRe.exec(text);
  if (at) {
    return {
      hour: +at[1]!,
      minute: 0,
      meridiem: parseMeridiem(at[2]),
      text: at[0],
      pattern: atRe,
    };
  }

  // A bare 24-hour stamp such as "14:30" with no "at".
  const stamp = /(?:^|\s)(2[0-3]|[01]\d):([0-5]\d)(?!\d)/.exec(text);
  if (stamp) {
    return {
      hour: +stamp[1]!,
      minute: +stamp[2]!,
      meridiem: null,
      text: stamp[0],
      pattern: /(?:\b|\s)(?:2[0-3]|[01]\d):[0-5]\d(?!\d)/,
    };
  }

  // A bare hour in the unambiguous 10-23 range.
  const bareHour = /(?:^|\s)(1\d|2[0-3])(?!\d|:)/.exec(text);
  if (bareHour) {
    return {
      hour: +bareHour[1]!,
      minute: 0,
      meridiem: null,
      text: bareHour[0],
      pattern: /(?:\b|\s)(?:1\d|2[0-3])(?!\d|:)/,
    };
  }

  return null;
}

function parseMeridiem(token: string | undefined): "am" | "pm" | null {
  if (!token) return null;
  const s = token.replace(/\s/g, "");
  if (s === "pm" || s.startsWith("ب")) return "pm";
  if (s === "am" || s.startsWith("ق")) return "am";
  return null;
}

function findDuration(text: string): number | null {
  // `text` is already lower-cased with Latin digits.
  const units: [RegExp, number][] = [
    [new RegExp("(\\d+)\\s*" + word(["ساعت", "hours?", "hrs?", "h"]), "u"), 60],
    [new RegExp("(\\d+)\\s*" + word(["دقیقه", "minutes?", "mins?", "min", "m"]), "u"), 1],
  ];
  for (const [pattern, factor] of units) {
    const m = pattern.exec(text);
    if (m?.[1]) return Math.min(24 * 60, +m[1] * factor);
  }
  if (wordPattern(["نیم ساعت", "نیمساعت", "half an hour", "half hour"]).test(text)) {
    return 30;
  }
  return null;
}

const TITLE_NOISE = [
  "فردا", "امروز", "دیروز", "پس فردا", "پریروز",
  "tomorrow", "today", "yesterday", "noon", "midday",
  "ظهر", "ساعت", "at", "بعد از", "در",
];

function cleanTitle(text: string): string {
  return text
    .replace(/\s+/g, " ")
    .replace(new RegExp("^" + word(["با", "و", "در", "in", "at", "on", "the", "را"]), "u"), "")
    .replace(
      new RegExp(word(TITLE_NOISE), "gu"),
      " ",
    )
    .replace(
      /\d{1,2}[:.]\d{2}\s*(?:am|pm|ق\.?\s*ظ|ب\.?\s*ظ)?/g,
      " ",
    )
    .replace(
      new RegExp(word(["در", "بعد از", "in", "after"]) + "\\s+\\d{1,3}\\s*" + word(["روز", "days?", "d"]), "gu"),
      " ",
    )
    .replace(/\s+/g, " ")
    .replace(/^[-–—,:،\s]+|[-–—,:،\s]+$/g, "")
    .trim();
}

export { HORIZON_DAYS };
